import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Upload, 
  X, 
  Plus, 
  Save, 
  Trash2,
  ArrowLeft,
  ImageIcon,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const EditComic = () => {
  const { id } = useParams();
  const [comic, setComic] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    images: [],
    categories: [],
    tags: []
  });
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const { user, token, API } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchComic();
  }, [id]);

  const fetchComic = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/comics/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const comicData = response.data;
      
      // Check if user owns this comic
      if (comicData.author_id !== user.id) {
        toast.error('You can only edit your own comics');
        navigate('/dashboard');
        return;
      }
      
      setComic(comicData);
      setFormData({
        title: comicData.title || '',
        description: comicData.description || '',
        images: [...comicData.images],
        categories: [...comicData.categories],
        tags: [...comicData.tags]
      });
      
    } catch (error) {
      console.error('Error fetching comic:', error);
      toast.error('Failed to load comic');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, event.target.result]
          }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const moveImageUp = (index) => {
    if (index > 0) {
      const newImages = [...formData.images];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      setFormData(prev => ({ ...prev, images: newImages }));
    }
  };

  const moveImageDown = (index) => {
    if (index < formData.images.length - 1) {
      const newImages = [...formData.images];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      setFormData(prev => ({ ...prev, images: newImages }));
    }
  };

  const addCategory = () => {
    if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const removeCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (formData.images.length === 0) {
      setError('At least one image is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await axios.put(`${API}/comics/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Comic updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating comic:', error);
      setError(error.response?.data?.detail || 'Failed to update comic');
      toast.error('Failed to update comic');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this comic? This action cannot be undone.')) {
      return;
    }

    setSaving(true);

    try {
      await axios.delete(`${API}/comics/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Comic deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting comic:', error);
      toast.error('Failed to delete comic');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-xl font-medium text-gray-600">Loading comic...</div>
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <Alert className="max-w-md">
          <AlertDescription>Comic not found or you don't have permission to edit it.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 animate-fade-in-up">
            <div>
              <h1 className="text-4xl font-bold gradient-text mb-4">Edit Comic</h1>
              <p className="text-gray-600">Update your comic details and manage slides</p>
            </div>
            
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleDelete}
                variant="destructive"
                disabled={saving}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>

          <Card className="glass-card animate-slide-in-right">
            <CardHeader>
              <CardTitle className="flex items-center">
                <ImageIcon className="w-5 h-5 mr-2" />
                {comic.series_name ? `${comic.series_name} - Episode ${comic.episode_number}` : 'Standalone Comic'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Title & Description */}
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Comic Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter comic title"
                    className="form-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your comic..."
                    className="form-input resize-none"
                    rows={4}
                  />
                </div>
              </div>

              {/* Enhanced Slide Management */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-lg font-semibold">Episode Slides Management</Label>
                    <p className="text-sm text-gray-600 mt-1">
                      {formData.images.length} slides • Drag to reorder • Click to manage
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="add-images"
                    />
                    <label
                      htmlFor="add-images"
                      className="cursor-pointer inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Slides
                    </label>
                  </div>
                </div>

                {formData.images.length > 0 ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 text-blue-800 mb-2">
                        <ImageIcon className="w-4 h-4" />
                        <span className="font-medium">Slide Management Tips</span>
                      </div>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Use ↑↓ buttons to reorder slides</li>
                        <li>• Each slide will be a page in your comic</li>
                        <li>• Readers will scroll vertically through slides</li>
                        <li>• First slide will be the episode thumbnail</li>
                      </ul>
                    </div>

                    <div className="space-y-3">
                      {formData.images.map((image, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-purple-300 transition-colors">
                          <div className="flex items-start space-x-4">
                            <div className="relative">
                              <img
                                src={image}
                                alt={`Slide ${index + 1}`}
                                className="w-32 h-32 object-cover rounded-lg flex-shrink-0 border"
                              />
                              <div className="absolute -top-2 -left-2 bg-purple-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                                {index + 1}
                              </div>
                              {index === 0 && (
                                <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs font-bold rounded-full px-2 py-1">
                                  Cover
                                </div>
                              )}
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold text-gray-800">
                                    Slide {index + 1} {index === 0 ? '(Episode Cover)' : ''}
                                  </h4>
                                  <p className="text-sm text-gray-500">
                                    This will be page {index + 1} in the comic reader
                                  </p>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  <Button
                                    onClick={() => moveImageUp(index)}
                                    disabled={index === 0}
                                    variant="outline"
                                    size="sm"
                                    title="Move up"
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    onClick={() => moveImageDown(index)}
                                    disabled={index === formData.images.length - 1}
                                    variant="outline"
                                    size="sm"
                                    title="Move down"
                                  >
                                    ↓
                                  </Button>
                                  <Button
                                    onClick={() => removeImage(index)}
                                    variant="destructive"
                                    size="sm"
                                    title="Delete slide"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-xs text-gray-600">
                                  <strong>Reader Experience:</strong> Readers will see this image in full-screen, 
                                  vertical scrolling mode. Make sure text and details are clearly visible.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 text-green-800 mb-2">
                        <Loader2 className="w-4 h-4" />
                        <span className="font-medium">Preview Mode</span>
                      </div>
                      <p className="text-sm text-green-700">
                        Your episode has {formData.images.length} slides. Readers will experience these as full-width, 
                        vertically scrolling pages with smooth transitions between slides.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                    <ImageIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-600 mb-2">No Slides Yet</h3>
                    <p className="text-gray-500 mb-6">
                      Add images to create slides for your comic episode. Each image will become a page in the reader.
                    </p>
                    <label
                      htmlFor="add-images"
                      className="cursor-pointer inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Upload First Slide
                    </label>
                  </div>
                )}
              </div>

              {/* Categories */}
              <div className="space-y-4">
                <Label>Categories</Label>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.categories.map((category) => (
                    <Badge key={category} variant="secondary" className="category-tag">
                      {category}
                      <button
                        type="button"
                        onClick={() => removeCategory(category)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Add category"
                    className="form-input flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                  />
                  <Button type="button" onClick={addCategory} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-4">
                <Label>Tags</Label>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="outline">
                      #{tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    className="form-input flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving || formData.images.length === 0}
                  className="btn-primary"
                  size="lg"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EditComic;